import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Truck, Shield, Clock } from 'lucide-react';
import ProductCard from '@/components/ProductCard';
import NewsletterForm from '@/components/NewsletterForm';
import SEO from '@/components/SEO';
import { EVERYDAY_CATEGORY_ID } from '@/lib/categories';
import { useLanguage } from '@/contexts/LanguageContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Textes propres à l'accueil. Le dictionnaire de LanguageContext regroupe les
// libellés partagés ; la prose d'une page vit à côté de sa mise en page.
const TEXTES = {
  fr: {
    heroTitre: "Découvrez les saveurs authentiques d'Afrique",
    heroSous: 'Votre épicerie africaine en ligne en Belgique. Produits frais, qualité garantie.',
    voirProduits: 'Voir nos produits',
    enSavoirPlus: 'En savoir plus',
    rayonTitre: 'Produits du quotidien',
    rayonSous: "Ce que l'on achète toutes les semaines : condiments, légumes, poissons, riz, huiles.",
    voirRayon: 'Voir tout le rayon →',
    voirCatalogue: 'ou parcourir tout le catalogue',
    appelTitre: 'Commandez maintenant et recevez chez vous',
    appelSous: 'Livraison rapide dans toute la Belgique. Produits frais et authentiques.',
    commander: 'Commander maintenant',
    newsletterTitre: 'Restez informé de nos nouveautés',
    newsletterSous: 'Inscrivez-vous à notre newsletter pour recevoir nos offres exclusives',
    altHero: 'Étal de fruits et légumes africains sur un marché',
    altFamille: 'Famille africaine réunie autour d’un repas',
    seoTitre: 'AfriOne BD - Épicerie africaine en ligne, livraison à Liège et en Belgique',
    seoDescription: 'Commandez vos produits africains en ligne : poissons, viandes, épices, farines, huiles et surgelés. Livraison rapide à Liège, en périphérie et partout en Belgique.',
    seoPartageTitre: 'AfriOne BD - Épicerie africaine en ligne',
    seoPartageDescription: 'Poissons, viandes, épices et produits africains livrés chez vous à Liège et partout en Belgique.',
    seoMotsCles: 'épicerie africaine, produits africains, Belgique, livraison, manioc, plantain',
  },
  en: {
    heroTitre: 'Discover the authentic flavours of Africa',
    heroSous: 'Your African grocery store online in Belgium. Fresh products, guaranteed quality.',
    voirProduits: 'Browse our products',
    enSavoirPlus: 'Learn more',
    rayonTitre: 'Everyday essentials',
    rayonSous: 'What we buy every week: seasonings, vegetables, fish, rice, oils.',
    voirRayon: 'See the whole aisle →',
    voirCatalogue: 'or browse the full catalogue',
    appelTitre: 'Order now and have it delivered',
    appelSous: 'Fast delivery throughout Belgium. Fresh, authentic products.',
    commander: 'Order now',
    newsletterTitre: 'Keep up with our new arrivals',
    newsletterSous: 'Subscribe to our newsletter to receive our exclusive offers',
    altHero: 'African fruit and vegetable market stall',
    altFamille: 'African family gathered around a meal',
    seoTitre: 'AfriOne BD - African grocery online, delivery in Liège and across Belgium',
    seoDescription: 'Order African products online: fish, meat, spices, flours, oils and frozen food. Fast delivery in Liège, the outskirts and throughout Belgium.',
    seoPartageTitre: 'AfriOne BD - African grocery online',
    seoPartageDescription: 'Fish, meat, spices and African products delivered to your door in Liège and across Belgium.',
    seoMotsCles: 'african grocery, african food, Belgium, delivery, cassava, plantain',
  },
};

// Nombre de cartes du rayon sur l'accueil.
const CARTES_ACCUEIL = 12;

// Un produit par famille à tour de rôle : douze cartes de la même famille ne
// donnent pas l'impression d'entrer dans une épicerie. Même règle que le
// rayon côté serveur (backend/everyday.py).
function alternerLesFamilles(produits) {
  const files = new Map();
  produits.forEach((p) => {
    if (!files.has(p.category)) files.set(p.category, []);
    files.get(p.category).push(p);
  });
  const resultat = [];
  while ([...files.values()].some((f) => f.length)) {
    files.forEach((file) => {
      if (file.length) resultat.push(file.shift());
    });
  }
  return resultat;
}

export default function Home() {
  const { language } = useLanguage();
  const x = TEXTES[language] || TEXTES.fr;
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Rayon « Produits du quotidien » : ce que le client voit en arrivant, comme
  // s'il entrait dans le magasin. Sélection du commerçant + meilleures ventes,
  // composée côté serveur.
  //
  // Tant que le rayon est court — rien de sélectionné, ou seulement les
  // quelques produits entrés par les ventes — on le complète avec le catalogue
  // pour ne pas accueillir le visiteur avec trois ou quatre cartes. Le rayon
  // reste en tête ; le complément alterne les familles, comme le rayon.
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const rayon = (await axios.get(`${API}/api/products?category=${EVERYDAY_CATEGORY_ID}`)).data;
        if (rayon.length >= CARTES_ACCUEIL) {
          setFeaturedProducts(rayon);
          return;
        }
        const catalogue = (await axios.get(`${API}/api/products`)).data;
        const deja = new Set(rayon.map((p) => p.id));
        const complement = alternerLesFamilles(
          catalogue.filter((p) => !deja.has(p.id) && p.stock > 0)
        );
        setFeaturedProducts([...rayon, ...complement].slice(0, CARTES_ACCUEIL));
      } catch (error) {
        // Error handled silently - will show loading state
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, []);
  
  const features = [
    { id: 'delivery', icon: Truck, title: 'Livraison rapide', titleEn: 'Fast Delivery', desc: 'Partout en Belgique', descEn: 'Throughout Belgium' },
    { id: 'quality', icon: Shield, title: 'Qualité garantie', titleEn: 'Quality Guaranteed', desc: 'Produits frais', descEn: 'Fresh products' },
    { id: 'support', icon: Clock, title: 'Service client', titleEn: 'Customer Service', desc: '7j/7 disponible', descEn: 'Available 7 days' },
  ];
  
  return (
    <div className="min-h-screen" data-testid="home-page">
      <SEO 
        title={x.seoTitre}
        description={x.seoDescription}
        ogTitle={x.seoPartageTitre}
        ogDescription={x.seoPartageDescription}
        twitterTitle={x.seoPartageTitre}
        twitterDescription={x.seoPartageDescription}
        keywords={x.seoMotsCles}
        canonical="https://afrionebd.eu/"
      />
      {/* Hero Section */}
      {/* data-hero : l'en-tête s'en sert pour savoir jusqu'où il survole
          une image sombre, et adapter son verre en conséquence. */}
      <section data-hero className="relative min-h-[400px] sm:min-h-[500px] lg:min-h-[600px] flex items-center justify-center overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="/images/accueil-hero.jpg"
            alt={x.altHero}
            className="w-full h-full object-cover"
          />
          {/* Voile de lisibilité. Le dégradé latéral ne convient qu'au grand
              écran, où le texte est aligné à gauche : en dessous il est centré
              sur toute la largeur et déborde sur la partie claire de la photo,
              d'autant plus depuis que celle-ci est lumineuse. On assombrit
              donc uniformément jusqu'à lg, où le dégradé d'origine reprend.
              Les valeurs sont les plus basses qui gardent le sous-titre au-dessus
              du seuil AA de 4,5 : mesuré 5,3 en mobile et 4,6 en grand écran. */}
          <div className="absolute inset-0 bg-text_primary/55 lg:bg-gradient-to-r lg:from-text_primary/85 lg:via-text_primary/65 lg:to-transparent" />
        </div>
        
        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20 text-center lg:text-left">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="lg:max-w-2xl"
          >
            <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-white mb-3 sm:mb-5 leading-tight">
              {x.heroTitre}
            </h1>
            <p className="text-sm sm:text-base lg:text-lg text-white/90 mb-6 sm:mb-8">
              {x.heroSous}
            </p>
            {/* items-center : en colonne (mobile), un enfant de flex s'étire sur
                toute la largeur par défaut ; les boutons prenaient tout l'écran. */}
            <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4 justify-center lg:justify-start">
              <Link
                to="/products"
                className="px-6 py-3 sm:px-8 sm:py-4 bg-primary text-white rounded-full font-semibold text-sm sm:text-base hover:bg-primary_hover transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105"
                data-testid="hero-shop-button"
              >
                {x.voirProduits}
              </Link>
              <Link
                to="/about"
                className="px-6 py-3 sm:px-8 sm:py-4 bg-white/10 backdrop-blur-md text-white border-2 border-white/30 rounded-full font-semibold text-sm sm:text-base hover:bg-white/20 transition-all duration-300"
              >
                {x.enSavoirPlus}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="py-8 sm:py-12 lg:py-16 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-6">
            {features.map((feature) => (
              <motion.div
                key={feature.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="flex items-center gap-3 sm:gap-4 p-4 sm:p-6 bg-white rounded-2xl shadow-sm"
                data-testid={`feature-${feature.id}`}
              >
                <div className="w-10 h-10 sm:w-14 sm:h-14 rounded-2xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <feature.icon className="w-5 h-5 sm:w-7 sm:h-7 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold text-text_primary text-sm sm:text-lg mb-0.5 sm:mb-1">
                    {language === 'en' ? feature.titleEn : feature.title}
                  </h3>
                  <p className="text-text_secondary text-xs sm:text-sm">
                    {language === 'en' ? feature.descEn : feature.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Featured Products */}
      <section className="py-10 sm:py-14 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-center mb-6 sm:mb-10 lg:mb-12"
          >
            <h2 className="text-xl sm:text-3xl lg:text-4xl font-bold text-text_primary mb-2 sm:mb-4">
              {x.rayonTitre}
            </h2>
            <p className="text-sm sm:text-base text-text_secondary max-w-2xl mx-auto">
              {x.rayonSous}
            </p>
          </motion.div>
          
          {loading ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 items-start gap-x-2 sm:gap-x-3 gap-y-0">
              {[...Array(4)].map((_, i) => (
                <div key={`skeleton-${i}`} className="animate-pulse pb-5">
                  <div className="w-full aspect-square bg-gray-200 mb-2" />
                  <div className="h-3 bg-gray-200 w-3/4 mb-2" />
                  <div className="h-5 bg-gray-200 w-1/2" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 items-start gap-x-2 sm:gap-x-3 gap-y-0" data-testid="featured-products-grid">
              {featuredProducts.slice(0, 12).map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          )}
          
          <div className="text-center mt-8 sm:mt-12">
            <Link
              to={`/products?category=${EVERYDAY_CATEGORY_ID}`}
              className="inline-block px-6 py-3 sm:px-8 sm:py-4 bg-primary text-white rounded-full font-semibold text-sm sm:text-base hover:bg-primary_hover transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
            >
              {x.voirRayon}
            </Link>
            <Link
              to="/products"
              className="block mt-3 text-sm text-text_secondary hover:text-primary transition-colors"
            >
              {x.voirCatalogue}
            </Link>
          </div>
        </div>
      </section>
      
      {/* CTA Section — overflow-hidden : les deux blocs entrent en glissant
          latéralement, et leur position de départ débordait la page tant que
          l'animation ne s'était pas déclenchée. */}
      <section className="py-10 sm:py-14 lg:py-20 bg-gradient-to-br from-primary to-primary_hover overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-xl sm:text-3xl lg:text-4xl font-bold text-white mb-3 sm:mb-5">
                {x.appelTitre}
              </h2>
              <p className="text-sm sm:text-base lg:text-lg text-white/90 mb-6 sm:mb-8">
                {x.appelSous}
              </p>
              <Link
                to="/products"
                className="inline-block px-6 py-3 sm:px-8 sm:py-4 bg-white text-primary rounded-full font-semibold text-sm sm:text-base hover:bg-gray-100 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105"
              >
                {x.commander}
              </Link>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
            <img
              src="/images/accueil-famille.jpg"
              alt={x.altFamille}
              className="w-full h-48 sm:h-72 lg:h-96 object-cover rounded-3xl shadow-2xl"
            />
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Newsletter */}
      <section className="py-8 sm:py-12 lg:py-16 bg-background">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-text_primary mb-2 sm:mb-4">
              {x.newsletterTitre}
            </h2>
            <p className="text-sm sm:text-base text-text_secondary mb-5 sm:mb-8">
              {x.newsletterSous}
            </p>
            <NewsletterForm />
          </motion.div>
        </div>
      </section>
    </div>
  );
}